import React, { useEffect, useState, useCallback } from 'react';
import { MapPin, RefreshCw, Navigation, Power, Gauge, X, Route } from 'lucide-react';
import LeafletMap from '../../components/LeafletMap';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';

const markerColor = (v) => {
  if (!v.ignition) return '#94a3b8';            // off → grey
  if ((v.speed_kmh || 0) >= 3) return '#16a34a'; // moving → green
  return '#f59e0b';                              // idle → amber
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const StatusDot = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />{label}
  </span>
);

function RouteHistoryModal({ vehicle, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiGet(`/gps/vehicles/${vehicle.vehicle_id}/history`, { interval: '5m' })
      .then(setData).catch(() => toast.error('Could not load route history'))
      .finally(() => setLoading(false));
  }, [vehicle.vehicle_id]);

  const polyline = (data?.points || []).map((p) => [p.lat, p.lng]);
  const markers = [];
  if (data?.points?.length) {
    const f = data.points[0], l = data.points[data.points.length - 1];
    markers.push({ id: 'start', lat: f.lat, lng: f.lng, color: '#16a34a', label: `Start · ${fmtTime(f.ts)}`, popupHtml: `<b>Start</b><br/>${fmtTime(f.ts)}` });
    markers.push({ id: 'end', lat: l.lat, lng: l.lng, color: '#dc2626', label: `Now · ${fmtTime(l.ts)}`, popupHtml: `<b>Latest</b><br/>${fmtTime(l.ts)}` });
  }
  (data?.stops || []).forEach((s, i) => markers.push({
    id: `stop-${i}`, lat: s.lat, lng: s.lng, color: '#f59e0b',
    label: `Stop · ${s.duration_minutes} min`, popupHtml: `<b>Stop</b><br/>${fmtTime(s.ts)}<br/>${s.duration_minutes} min`,
  }));

  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" data-testid="route-history-modal">
      <div className="bg-white rounded-sm w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Route size={16} className="text-[#1F7A6E]" />
            <h3 className="font-display font-bold">Route — {vehicle.vehicle_number} <span className="text-slate-400 font-normal text-sm">(last 24h)</span></h3>
          </div>
          <button onClick={onClose} data-testid="route-history-close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="h-[420px] flex items-center justify-center text-slate-400 text-sm">Loading route…</div>
          ) : (data?.points?.length || 0) < 2 ? (
            <div className="h-[420px] flex items-center justify-center text-slate-400 text-sm" data-testid="route-empty">No movement recorded in the last 24 hours.</div>
          ) : (
            <>
              <LeafletMap markers={markers} polyline={polyline} height="420px" />
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                <span>{data.count} points</span><span>{data.stops.length} stop(s) &gt; 5 min</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GPSLiveMap() {
  const [vehicles, setVehicles] = useState(null);
  const [selected, setSelected] = useState(null);
  const [routeFor, setRouteFor] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(() => {
    apiGet('/gps/vehicles/live').then((d) => {
      setVehicles(d.vehicles || []);
      setRefreshedAt(new Date());
    }).catch(() => setVehicles([]));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // 60s poll — meant for an always-on office monitor
    return () => clearInterval(t);
  }, [load]);

  const positioned = (vehicles || []).filter((v) => v.gps_lat != null && v.gps_lng != null);
  const markers = positioned.map((v) => ({
    id: v.vehicle_id, lat: v.gps_lat, lng: v.gps_lng, color: markerColor(v), label: v.vehicle_number,
    popupHtml: `<b>${v.vehicle_number || '—'}</b><br/>${v.driver_name || 'No driver'}<br/>${v.ignition ? `${Math.round(v.speed_kmh || 0)} km/h` : 'Ignition off'}`,
  }));

  if (vehicles === null) {
    return <div className="p-8 text-slate-400 text-sm">Loading GPS map…</div>;
  }
  if (vehicles.length === 0) {
    return (
      <div className="p-8" data-testid="gps-empty-state">
        <div className="max-w-md mx-auto text-center border border-dashed border-slate-300 rounded-sm p-10">
          <MapPin size={32} className="mx-auto text-slate-300 mb-3" />
          <h3 className="font-display font-bold text-lg">No GPS-enabled vehicles yet</h3>
          <p className="text-sm text-slate-500 mt-1">Enable GPS tracking on a vehicle in <b>Vehicle Master</b> to see it here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6" data-testid="gps-live-map-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-400">Fuel & Fleet</div>
          <h1 className="font-display font-bold text-2xl flex items-center gap-2"><Navigation size={20} className="text-[#1F7A6E]" /> GPS Live Map</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex gap-3">
            <StatusDot color="#16a34a" label="Moving" />
            <StatusDot color="#f59e0b" label="Idle" />
            <StatusDot color="#94a3b8" label="Off" />
          </div>
          <button onClick={load} data-testid="gps-refresh" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm border border-slate-300 hover:bg-slate-50">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-2">
          <LeafletMap markers={markers} height="540px" onMarkerClick={(id) => setSelected(positioned.find((v) => v.vehicle_id === id))} />
          {refreshedAt && <div className="text-[11px] text-slate-400 px-2 py-1">Updated {refreshedAt.toLocaleTimeString('en-IN')} · auto-refresh 60s</div>}
        </div>

        <div className="space-y-2">
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 px-1">{positioned.length} of {vehicles.length} reporting</div>
          {vehicles.map((v) => (
            <button key={v.vehicle_id} onClick={() => setSelected(v)} data-testid={`gps-vehicle-${v.vehicle_id}`}
              className={`w-full text-left bg-white border rounded-sm px-3 py-2.5 hover:border-[#1F7A6E] transition-colors ${selected?.vehicle_id === v.vehicle_id ? 'border-[#1F7A6E] ring-1 ring-[#1F7A6E]/30' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-sm">{v.vehicle_number}</span>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: markerColor(v) }} />
              </div>
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                <span>{v.driver_name || 'No driver'}</span>
                <span className="inline-flex items-center gap-1"><Power size={11} /> {v.ignition ? 'On' : 'Off'}</span>
                <span className="inline-flex items-center gap-1"><Gauge size={11} /> {Math.round(v.speed_kmh || 0)} km/h</span>
              </div>
              {v.gps_lat == null && <div className="text-[11px] text-amber-600 mt-1">Awaiting first GPS ping</div>}
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div className="fixed bottom-4 right-4 z-[500] w-80 bg-white border border-slate-200 rounded-sm shadow-xl p-4" data-testid="gps-vehicle-detail">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-bold">{selected.vehicle_number}</h3>
            <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
          </div>
          <dl className="text-sm space-y-1">
            <div className="flex justify-between"><dt className="text-slate-400">Driver</dt><dd>{selected.driver_name || '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">Project</dt><dd>{selected.project_name || '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">Speed</dt><dd>{Math.round(selected.speed_kmh || 0)} km/h</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">Ignition</dt><dd>{selected.ignition ? 'On' : 'Off'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">Status</dt><dd className="capitalize">{selected.gps_status}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">Last seen</dt><dd className="text-right text-xs">{fmtTime(selected.gps_last_seen)}</dd></div>
          </dl>
          <button onClick={() => setRouteFor(selected)} disabled={selected.gps_lat == null} data-testid="gps-view-route"
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-sm bg-[#1F7A6E] text-white hover:bg-[#2f3a5c] disabled:opacity-40">
            <Route size={13} /> View Route History
          </button>
        </div>
      )}

      {routeFor && <RouteHistoryModal vehicle={routeFor} onClose={() => setRouteFor(null)} />}
    </div>
  );
}
